import { Box, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { StatStrip } from "../../../components/UIToolkit/molecules/StatStrip";
import { FONT_SIZE, ICON_SIZE } from "../../../theme/tokens";
import type { PbjPlanCost } from "../../../utils/models";
import { PlanCostMeter } from "./PlanCostMeter";
import { formatCurrency, spendBudgetToDate } from "./planValue.utils";

type Props = {
	cost: PbjPlanCost;
	/** The plan also adds hours — a swap, judged on its net rather than on savings alone. */
	isMixed: boolean;
	/** Whether the plan has spent / saved anything yet — see `PlanCostPanel`. */
	hasSpend: boolean;
	hasSavings: boolean;
	/** Share of the plan elapsed, already printed as a whole percent. */
	share: string;
};

/**
 * The cost panel's body for a plan with cuts: the net figure it leads with, the price/savings stats on
 * a swap, and the spend and savings meters as a matched pair.
 */
export const PlanCostAndSavingsBody: FC<Props> = ({ cost, isMixed, hasSpend, hasSavings, share }) => {
	const { t } = useTranslation();
	const netIsSavings = cost.netPrice < 0;
	const spendBudget = spendBudgetToDate(cost);
	// A cut-only plan budgets no spend, but staffing rising on a cut lever still spends. That spend is
	// shown against itself — a full bar in error — rather than hidden because there was no budget.
	const showSpendMeter = isMixed || hasSpend;

	return (
		<>
			{/* The one figure the panel leads with: what the plan nets out to. On a cut-only plan
			    that is simply its savings, so it is named as such rather than as a "net". */}
			<Box data-testid="pbj-model-plan-net">
				<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
					<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
						{t(!isMixed ? "pbj_model.tracker.total_savings" : netIsSavings ? "pbj_model.action_plan.net_savings" : "pbj_model.action_plan.net_cost")}
					</Typography>
					{isMixed && (
						<InfoIconWithTooltip title={t("pbj_model.action_plan.net_tooltip")} iconSx={{ fontSize: ICON_SIZE.PILL }} dataTestId="pbj-model-plan-net-info" />
					)}
				</Stack>
				<Typography
					sx={{
						fontSize: FONT_SIZE.TITLE_HERO,
						fontWeight: 800,
						lineHeight: 1.15,
						fontVariantNumeric: "tabular-nums",
						color: netIsSavings ? "success.dark" : "text.primary",
					}}
					data-testid="pbj-model-plan-net-price"
				>
					{formatCurrency(Math.abs(cost.netPrice))}
				</Typography>
			</Box>

			{isMixed && (
				<StatStrip
					dataTestId="pbj-model-plan-cost-stats"
					items={[
						{ label: t("pbj_model.tracker.total_price"), value: formatCurrency(cost.totalPrice), dataTestId: "pbj-model-plan-total-price" },
						{ label: t("pbj_model.tracker.total_savings"), value: formatCurrency(cost.totalSavings), dataTestId: "pbj-model-plan-total-savings" },
					]}
				/>
			)}

			<Stack spacing={2.5} sx={{ mt: "auto" }}>
				{showSpendMeter && (
					<PlanCostMeter
						label={t("pbj_model.tracker.spend_so_far")}
						value={formatCurrency(cost.spendSoFar)}
						amount={cost.spendSoFar}
						max={isMixed ? cost.totalPrice : cost.spendSoFar}
						expectedShare={isMixed ? cost.elapsedShare : undefined}
						// Judged on the spend side alone — the net verdict is the chip's job. A mixed plan whose
						// savings run ahead can be on budget overall while its additions overspend: that reads
						// as a warning beside a green chip, and as an error only when the net is over too.
						tone={cost.spendSoFar > spendBudget ? (cost.onBudget ? "warning" : "error") : "primary"}
						caption={t("pbj_model.tracker.budget_to_date", { budget: formatCurrency(spendBudget), share })}
						basis={t(hasSpend ? "pbj_model.tracker.spend_basis" : "pbj_model.tracker.spend_basis_none")}
						ariaLabel={t("pbj_model.tracker.spend_progress_aria")}
						dataTestId="pbj-model-plan-spend-meter"
					/>
				)}
				<PlanCostMeter
					label={t("pbj_model.tracker.savings_so_far")}
					value={formatCurrency(cost.savingsSoFar)}
					amount={cost.savingsSoFar}
					max={cost.totalSavings}
					expectedShare={cost.elapsedShare}
					tone="success"
					caption={t("pbj_model.tracker.savings_to_date", { savings: formatCurrency(cost.savingsToDate), share })}
					basis={t(hasSavings ? "pbj_model.tracker.savings_basis" : "pbj_model.tracker.savings_basis_none")}
					ariaLabel={t("pbj_model.tracker.savings_progress_aria")}
					dataTestId="pbj-model-plan-savings-meter"
				/>
			</Stack>
		</>
	);
};
